import { __decorate, __param } from '../node_modules/tslib/tslib.es6.js';
import '../vscode-services/missing-services.js';
import { SyncDescriptor } from 'monaco-editor/esm/vs/platform/instantiation/common/descriptors.js';
import { FileService } from '../vscode/vs/platform/files/common/fileService.js';
import { ILogService } from 'monaco-editor/esm/vs/platform/log/common/log.js';
import { InMemoryFileSystemProvider } from '../vscode/vs/platform/files/common/inMemoryFilesystemProvider.js';
import { FileType } from '../vscode/vs/platform/files/common/files.js';
export { FileChangeType, FileSystemProviderCapabilities } from '../vscode/vs/platform/files/common/files.js';
import { createFileSystemProviderError, FileSystemProviderErrorCode, FileSystemProviderError, IFileService } from 'monaco-editor/esm/vs/platform/files/common/files.js';
export { FileSystemProviderError, FileSystemProviderErrorCode } from 'monaco-editor/esm/vs/platform/files/common/files.js';
import { Disposable, DisposableStore } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
import { joinPath } from 'monaco-editor/esm/vs/base/common/resources.js';
import { Event, Emitter } from 'monaco-editor/esm/vs/base/common/event.js';
export { HTMLFileSystemProvider } from 'monaco-editor/esm/vs/platform/files/browser/htmlFileSystemProvider.js';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api.js';
import '../vscode/vs/workbench/contrib/files/browser/files.contribution.js';
class File {
    constructor(getContent) {
        this.getContent = getContent;
        this.ctime = Date.now();
        this.mtime = Date.now();
        this.size = 0;
        this.type = FileType.File;
    }
}
const encoder = new TextEncoder();
const decoder = new TextDecoder();
class SimpleTextFileSystemProvider {
    constructor() {
        this.onDidChangeCapabilities = Event.None;
        this.capabilities = 2  | 1024 ;
        this.onDidChangeFile = Event.None;
        this.onDidWatchError = Event.None;
    }
    async readFile(resource) {
        try {
            const content = await this.getFileContent(resource);
            if (content != null) {
                return encoder.encode(content);
            }
        }
        catch (err) {
            throw createFileSystemProviderError(err, FileSystemProviderErrorCode.Unknown);
        }
        throw createFileSystemProviderError('file not found', FileSystemProviderErrorCode.FileNotFound);
    }
    async writeFile(resource, content, opts) {
        try {
            await this.setFileContent(resource, decoder.decode(content), opts);
        }
        catch (err) {
            throw createFileSystemProviderError(err, FileSystemProviderErrorCode.Unknown);
        }
    }
    watch() {
        return Disposable.None;
    }
    async stat(resource) {
        try {
            const content = await this.getFileContent(resource);
            if (content != null) {
                return {
                    type: FileType.File,
                    size: encoder.encode(content).length,
                    ctime: Date.now(),
                    mtime: Date.now()
                };
            }
        }
        catch (err) {
            throw createFileSystemProviderError(err, FileSystemProviderErrorCode.Unknown);
        }
        throw createFileSystemProviderError('file not found', FileSystemProviderErrorCode.FileNotFound);
    }
    async mkdir() {
    }
    async readdir(_directory) {
        return [];
    }
    delete() {
        throw createFileSystemProviderError('Not allowed', FileSystemProviderErrorCode.NoPermissions);
    }
    rename() {
        throw createFileSystemProviderError('Not allowed', FileSystemProviderErrorCode.NoPermissions);
    }
}
class TypescriptWorkerFileSystemProvider extends SimpleTextFileSystemProvider {
    async getFileContent(resource) {
        const typescript = monaco.languages.typescript;
        if (typescript == null) {
            return undefined;
        }
        const path = resource.path.slice(1);
        return typescript.typescriptDefaults.getExtraLibs()[path]?.content ?? typescript.javascriptDefaults.getExtraLibs()[path]?.content;
    }
    async setFileContent() { }
}
class ExtensionFileSystemProviderWithFileReadWriteCapability {
    constructor() {
        this.capabilities = 2  | 1024  | 2048 ;
        this.onDidChangeFile = Event.None;
        this.onDidChangeCapabilities = Event.None;
        this.files = new Map();
    }
    async stat(resource) {
        const file = this.files.get(( resource.toString()));
        if (file != null) {
            return file;
        }
        throw FileSystemProviderError.create('file not found', FileSystemProviderErrorCode.FileNotFound);
    }
    async readFile(resource) {
        const file = this.files.get(( resource.toString()));
        if (file != null) {
            const data = await file.getContent();
            if (data instanceof Uint8Array) {
                return data;
            }
            else {
                return encoder.encode(data);
            }
        }
        throw FileSystemProviderError.create('file not found', FileSystemProviderErrorCode.FileNotFound);
    }
    watch() {
        return Disposable.None;
    }
    registerFile(resource, getContent) {
        this.files.set(( resource.toString()), new File(getContent));
        return {
            dispose: () => {
                this.files.delete(( resource.toString()));
            }
        };
    }
    async writeFile() {
        throw createFileSystemProviderError('Not allowed', FileSystemProviderErrorCode.NoPermissions);
    }
    async mkdir() {
        throw createFileSystemProviderError('Not allowed', FileSystemProviderErrorCode.NoPermissions);
    }
    readdir() {
        throw createFileSystemProviderError('Not allowed', FileSystemProviderErrorCode.NoPermissions);
    }
    async delete() {
        throw createFileSystemProviderError('Not allowed', FileSystemProviderErrorCode.NoPermissions);
    }
    async rename() {
        throw createFileSystemProviderError('Not allowed', FileSystemProviderErrorCode.NoPermissions);
    }
}
const extensionFileSystemProvider = new ExtensionFileSystemProviderWithFileReadWriteCapability();
function isFullfiled(result) {
    return result.status === 'fulfilled';
}
class OverlayFileSystemProvider {
    constructor(_default) {
        this._default = _default;
        this.others = [];
        this.onDidChangeCapabilities = Event.any(...( this.delegates.map(delegate => delegate.onDidChangeCapabilities)));
        this._onDidChangeFile = new Emitter();
        this.onDidChangeFile = Event.any(this._default.onDidChangeFile, this._onDidChangeFile.event);
        this._onDidChangeOverlays = new Emitter();
        this.onDidChangeOverlays = this._onDidChangeOverlays.event;
        this.capabilities = 2  | 1024 ;
    }
    register(delegate) {
        this.others.push(delegate);
        const disposableStore = new DisposableStore();
        disposableStore.add(delegate.onDidChangeFile(e => {
            this._onDidChangeFile.fire(e);
        }));
        disposableStore.add({
            dispose: () => {
                const index = this.others.indexOf(delegate);
                if (index >= 0) {
                    this.others.splice(index, 1);
                    this._onDidChangeOverlays.fire();
                }
            }
        });
        this._onDidChangeOverlays.fire();
        return disposableStore;
    }
    get delegates() {
        return [this._default, ...this.others];
    }
    async readFromDelegates(caller) {
        let firstError;
        for (const delegate of this.delegates) {
            try {
                return await caller(delegate);
            }
            catch (err) {
                firstError ?? (firstError = err);
                if (err instanceof FileSystemProviderError && err.code === FileSystemProviderErrorCode.FileNotFound) {
                    continue;
                }
                throw err;
            }
        }
        throw firstError;
    }
    async writeToDelegates(caller) {
        const results = await Promise.allSettled(( this.delegates.map(async (delegate) => {
            if (((delegate.capabilities & 2048) ) === 0) {
                await caller(delegate);
            }
        })));
        if (!( results.some(result => result.status === 'fulfilled'))) {
            throw results[0].reason;
        }
    }
    async stat(resource) {
        return this.readFromDelegates(delegate => delegate.stat(resource));
    }
    async readFile(resource) {
        return this.readFromDelegates(delegate => delegate.readFile(resource));
    }
    async readdir(resource) {
        const results = await Promise.allSettled(( this.delegates.map(delegate => delegate.readdir(resource))));
        if (!( results.some(result => result.status === 'fulfilled'))) {
            throw results[0].reason;
        }
        return Object.entries(Object.fromEntries(( results.filter(isFullfiled).map(result => result.value)).flat()));
    }
    watch(resource, opts) {
        const store = new DisposableStore();
        for (const delegate of this.delegates) {
            store.add(delegate.watch(resource, opts));
        }
        return store;
    }
    async writeFile(resource, content, opts) {
        await this.writeToDelegates(delegate => delegate.writeFile(resource, content, opts));
    }
    async mkdir(resource) {
        await this.writeToDelegates(delegate => delegate.mkdir(resource));
    }
    async delete(resource, opts) {
        await this.writeToDelegates(delegate => delegate.delete(resource, opts));
    }
    async rename(from, to, opts) {
        await this.writeToDelegates(delegate => delegate.rename(from, to, opts));
    }
}
const fileSystemProvider = new OverlayFileSystemProvider(new InMemoryFileSystemProvider());
let MemoryFileService = class MemoryFileService extends FileService {
    constructor(logService) {
        super(logService);
        const userMemoryFileSystem = new InMemoryFileSystemProvider();
        this.registerProvider('user', userMemoryFileSystem);
        this.registerProvider('extension', extensionFileSystemProvider);
        this.registerProvider('cache', new InMemoryFileSystemProvider());
        this.registerProvider('logs', new InMemoryFileSystemProvider());
        let fileSystemProviderDisposable = this.registerProvider('file', fileSystemProvider);
        fileSystemProvider.onDidChangeOverlays(() => {
            fileSystemProviderDisposable.dispose();
            fileSystemProviderDisposable = this.registerProvider('file', fileSystemProvider);
        });
    }
};
MemoryFileService = __decorate([
    ( __param(0, ILogService))
], MemoryFileService);
function getServiceOverride() {
    return {
        [( IFileService.toString())]: new SyncDescriptor(MemoryFileService)
    };
}
function registerExtensionFile(extensionLocation, path, getContent) {
    return extensionFileSystemProvider.registerFile(joinPath(extensionLocation, path), getContent);
}
function registerFileSystemOverlay(provider) {
    return fileSystemProvider.register(provider);
}
registerFileSystemOverlay(new TypescriptWorkerFileSystemProvider());
export { FileType, SimpleTextFileSystemProvider, getServiceOverride as default, registerExtensionFile, registerFileSystemOverlay };
